import { getStageBadgeClass, getStageFilterClass, STAGE_ORDER } from "../../utils/leadStageBadge";
import { useState, useEffect, useMemo, useCallback } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { cn } from "../../utils/tailwindUtils";
import { RefreshCw, Search, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { CUSTOMERLEADSOURCEREPORT, USERS_SIMPLE_URL, LEADFORMLIST } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { useNavigate } from "react-router-dom";
import { usePersistedState } from "../../hooks/usePersistedState";
import StateFilterSelect from "../../components/common/StateFilterSelect";
import dayjs from "dayjs";
import TruncatedText from "../../components/ui/truncated-text";
import { formatDateTime } from "../../utils/datetime";

// ── Date presets ──────────────────────────────────────────────────────────────

const DATE_PRESETS = [
    { value: "all",        label: "All Time" },
    { value: "today",      label: "Today" },
    { value: "yesterday",  label: "Yesterday" },
    { value: "last_week",  label: "Last 7 Days" },
    { value: "last_month", label: "Last 30 Days" },
    { value: "this_month", label: "This Month" },
    { value: "custom",     label: "Custom Range" },
];

// Backend expects DD-MM-YYYY
const resolveDateRange = (preset, customFrom, customTo) => {
    const today = dayjs();
    const fmt   = d => d.format("DD-MM-YYYY");
    switch (preset) {
        case "today":      return { from_date: fmt(today),                          to_date: fmt(today) };
        case "yesterday":  return { from_date: fmt(today.subtract(1, "day")),       to_date: fmt(today.subtract(1, "day")) };
        case "last_week":  return { from_date: fmt(today.subtract(7,  "day")),      to_date: fmt(today) };
        case "last_month": return { from_date: fmt(today.subtract(30, "day")),      to_date: fmt(today) };
        case "this_month": return { from_date: fmt(today.startOf("month")), to_date: fmt(today) };
        case "custom":     return (customFrom && customTo)
            ? { from_date: dayjs(customFrom).format("DD-MM-YYYY"), to_date: dayjs(customTo).format("DD-MM-YYYY") }
            : {};
        default:           return {};
    }
};

const getDisplayLabel = (preset, customFrom, customTo) => {
    const today = dayjs();
    switch (preset) {
        case "today":      return today.format("DD MMM YYYY");
        case "yesterday":  return today.subtract(1, "day").format("DD MMM YYYY");
        case "last_week":  return `${today.subtract(7,  "day").format("DD MMM")} – ${today.format("DD MMM YYYY")}`;
        case "last_month": return `${today.subtract(30, "day").format("DD MMM")} – ${today.format("DD MMM YYYY")}`;
        case "this_month": return `${today.startOf("month").format("DD MMM")} – ${today.format("DD MMM YYYY")}`;
        case "custom":     return (customFrom && customTo)
            ? `${dayjs(customFrom).format("DD MMM")} – ${dayjs(customTo).format("DD MMM YYYY")}`
            : null;
        default:           return null;
    }
};


// ── Date parser for DD-MM-YYYY HH:MM AM / DD-MM-YYYY backend format ──────────


// ── main component ─────────────────────────────────────────────────────────────

const LeadSourcesReport = () => {
    const { fetch } = useApiMethods();
    const navigate  = useNavigate();

    const [allData,     setAllData]     = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [search,      setSearch]      = useState("");
    const [stageFilter, setStageFilter] = usePersistedState("lead_sources_stage",   null);
    const [datePreset,  setDatePreset]  = usePersistedState("lead_sources_preset",  "today");
    const [customFrom,  setCustomFrom]  = usePersistedState("lead_sources_from",    "");
    const [customTo,    setCustomTo]    = usePersistedState("lead_sources_to",      "");
    const [users,          setUsers]          = useState([]);
    const [selectedUserId, setSelectedUserId] = usePersistedState("lead_sources_user", "");
    const [stateFilter, setStateFilter] = usePersistedState("lead_sources_state", "");
    const [leadForms,      setLeadForms]      = useState([]);
    const [selectedFormId, setSelectedFormId] = usePersistedState("lead_sources_form", "");
    const [page,           setPage]           = useState(1);
    const [pageSize,    setPageSize]    = useState(25);

    const load = useCallback(() => {
        if (datePreset === "custom" && (!customFrom || !customTo)) return;
        setLoading(true);
        const { from_date, to_date } = resolveDateRange(datePreset, customFrom, customTo);
        const params = new URLSearchParams();
        if (from_date)      params.append("from_date",        from_date);
        if (to_date)        params.append("to_date",          to_date);
        if (selectedUserId) params.append("assigned_user_id", selectedUserId);
        if (stateFilter) params.append("state", stateFilter);
        if (selectedFormId) params.append("lead_form_id",     selectedFormId);
        const qs  = params.toString();
        const url = qs ? `${CUSTOMERLEADSOURCEREPORT}?${qs}` : CUSTOMERLEADSOURCEREPORT;
        fetch(url)
            .then(res => {
                if (res?.status === "success") {
                    setAllData(Array.isArray(res.data) ? res.data : []);
                } else {
                    ToastMsg("error", res?.message || "Failed to load report");
                }
            })
            .catch(() => ToastMsg("error", "Failed to load report"))
            .finally(() => setLoading(false));
    }, [datePreset, customFrom, customTo, selectedUserId, selectedFormId, stateFilter]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        fetch(USERS_SIMPLE_URL).then(res => {
            if (res?.status === "success") setUsers(res.data || []);
        }).catch(() => {});
        fetch(LEADFORMLIST).then(res => {
            if (res?.status === "success") setLeadForms(res.data || []);
        }).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { setPage(1); }, [stageFilter, search]);

    const stages = useMemo(() => {
        const present = new Set(allData.map(r => r.lead_stage).filter(Boolean));
        const ordered = STAGE_ORDER.filter(s => present.has(s));
        present.forEach(s => { if (!ordered.includes(s)) ordered.push(s); });
        return ordered;
    }, [allData]);

    const stageCounts = useMemo(() => {
        const counts = {};
        allData.forEach(r => {
            if (r.lead_stage) counts[r.lead_stage] = (counts[r.lead_stage] || 0) + 1;
        });
        return counts;
    }, [allData]);

    const filtered = useMemo(() => {
        let rows = allData;
        if (stageFilter) rows = rows.filter(r => r.lead_stage === stageFilter);
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            rows = rows.filter(r =>
                r.customer_name?.toLowerCase().includes(q) ||
                r.contact_number?.toLowerCase().includes(q) ||
                r.city?.toLowerCase().includes(q) ||
                r.lead_form_name__form_name?.toLowerCase().includes(q)
            );
        }
        return rows;
    }, [allData, stageFilter, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const paginated  = filtered.slice((page - 1) * pageSize, page * pageSize);

    return (
        <ContainerCard>
            {/* ── fixed top: header + filters ── */}
            <div className="flex-shrink-0">
            {/* ── header ── */}
            <div className="flex items-center justify-between mb-4 px-1">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Lead Meta Form</h1>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Where leads are coming from — lead form, city, and current stage
                    </p>
                </div>
                <Button onClick={load} size="sm" variant="outline" className="flex items-center gap-1 text-xs" disabled={loading}>
                    <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} />
                    Refresh
                </Button>
            </div>

            {/* ── date filter ── */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="relative">
                    <select
                        value={datePreset}
                        onChange={e => {
                            setDatePreset(e.target.value);
                            if (e.target.value !== "custom") { setCustomFrom(""); setCustomTo(""); }
                        }}
                        className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer"
                    >
                        {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                </div>
                {datePreset === "custom" && (
                    <>
                        <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                            className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400" />
                        <span className="text-xs text-gray-400">to</span>
                        <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                            className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400" />
                        <Button onClick={() => load()} size="sm" variant="outline" className="text-xs px-3" disabled={loading}>Apply</Button>
                    </>
                )}
                <div className="relative">
                    <select value={selectedFormId} onChange={e => setSelectedFormId(e.target.value)}
                        className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer"
                    >
                        <option value="">All Lead Forms</option>
                        {leadForms.map(f => (
                            <option key={f.id} value={f.id}>{f.form_name}</option>
                        ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                </div>
                <div className="relative">
                    <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
                        className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer"
                    >
                        <option value="">All Reps</option>
                        {users.map(u => {
                            const name = [u.login_fname, u.login_lname].filter(Boolean).join(" ") || u.login_name;
                            return <option key={u.id} value={u.id}>{name}</option>;
                        })}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                </div>
                <StateFilterSelect value={stateFilter} onChange={setStateFilter} />
                {datePreset !== "all" && (
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">
                        {getDisplayLabel(datePreset, customFrom, customTo)}
                    </span>
                )}
            </div>

            {/* ── stage filter badges ── */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <button
                    onClick={() => setStageFilter(null)}
                    className={cn(
                        "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                        !stageFilter
                            ? "bg-gray-700 text-white border-gray-800"
                            : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700"
                    )}
                >
                    All · {allData.length}
                </button>
                {stages.map(stage => (
                    <button
                        key={stage}
                        onClick={() => setStageFilter(stageFilter === stage ? null : stage)}
                        className={cn(
                            "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                            getStageFilterClass(stage, stageFilter === stage)
                        )}
                    >
                        {stage} · {stageCounts[stage] ?? 0}
                    </button>
                ))}
                <div className="ml-auto flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1 bg-white dark:bg-gray-800">
                    <Search className="h-3 w-3 text-gray-400" />
                    <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
                        className="text-xs bg-transparent outline-none text-gray-700 dark:text-gray-300 w-32" />
                </div>
            </div>
            </div>

            {/* ── scrollable table + fixed pagination ── */}
            {loading ? (
                <div className="flex items-center justify-center flex-1">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary mx-auto" />
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading report…</p>
                    </div>
                </div>
            ) : (
                <div className="flex-1 min-h-0 flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="flex-1 min-h-0 overflow-auto">
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 z-10">
                                <tr className="bg-gray-50 dark:bg-gray-700/50 text-left">
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 w-8">#</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Created</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Customer</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Mobile No</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">City</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Lead Form</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Stage</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Assigned To</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="text-center py-10 text-xs text-gray-400 dark:text-gray-500">No leads found</td>
                                    </tr>
                                ) : (
                                    paginated.map((row, idx) => (
                                        <tr
                                            key={row.id || idx}
                                            className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer transition-colors"
                                            onClick={() => navigate("/customers/customerdetails", { state: { formEdit: true, row: { id: row.id } } })}
                                        >
                                            <td className="px-3 py-2.5 text-gray-400 dark:text-gray-500">{(page - 1) * pageSize + idx + 1}</td>
                                            <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                                {formatDateTime(row.created_date)}
                                            </td>
                                            <td className="px-3 py-2.5 font-medium text-gray-800 dark:text-gray-200 max-w-[180px]">
    <TruncatedText text={row.customer_name} />
</td>
                                            <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                                {row.contact_number || "—"}
                                            </td>
                                            <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">
                                                {row.city || "—"}
                                            </td>
                                            <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 max-w-[140px] truncate">
                                                {row.lead_form_name__form_name || "—"}
                                            </td>
                                            <td className="px-3 py-2.5">
                                                {row.lead_stage ? (
                                                    <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-medium", getStageBadgeClass(row.lead_stage))}>
                                                        {row.lead_stage}
                                                    </span>
                                                ) : "—"}
                                            </td>
                                            <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 max-w-[120px] truncate">{row.assigned_to_name || "—"}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex-shrink-0 px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-gray-500 dark:text-gray-400">Rows per page</span>
                            <select
                                value={pageSize}
                                onChange={e => setPageSize(Number(e.target.value))}
                                className="text-[10px] border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none"
                            >
                                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                {filtered.length === 0 ? '0' : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, filtered.length)}`} of {filtered.length}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setPage(1)} disabled={page === 1} className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">«</button>
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronLeft className="h-3 w-3" /></button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                                .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i-1] > 1) acc.push('…'); acc.push(p); return acc; }, [])
                                .map((p, i) => p === '…'
                                    ? <span key={`e${i}`} className="px-1 text-[10px] text-gray-400">…</span>
                                    : <button key={p} onClick={() => setPage(p)} className={cn("min-w-[22px] px-1.5 py-0.5 text-[10px] rounded border transition-colors", p === page ? "bg-indigo-600 text-white border-indigo-600" : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700")}>{p}</button>
                                )
                            }
                            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronRight className="h-3 w-3" /></button>
                            <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">»</button>
                        </div>
                    </div>
                </div>
            )}
        </ContainerCard>
    );
};

export default LeadSourcesReport;
